import React, { Fragment, useCallback, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { PlaybookTransformItem, PlaybookSpreading } from '../../../types/playbook';
import { EventSchema, Parameter } from '../../../types/schema';
import PropertyPathAutocomplete from '../../common/PropertyPathAutocomplete';
import ReactDOM from 'react-dom'; // Added ReactDOM
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { usePortalRoot } from '@contexts/PortalRootContext'; // Added usePortalRoot
import { useTooltipPosition } from '@hooks/useTooltipPosition'; // Added useTooltipPosition

import { PlaybookEventConfig } from '@/types/playbook';

interface SpreadingEditorProps {
  transform: PlaybookTransformItem;
  onUpdate: (updates: Partial<PlaybookTransformItem>) => void;
  availableProperties?: Parameter[];
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  outputSchema?: any;
  onCloseEditor: () => void;
  usedInputProperties?: string[];
}

const SpreadingEditor: React.FC<SpreadingEditorProps> = ({
  transform,
  onUpdate,
  availableProperties = [],
  uiEventConfigs,
  onCloseEditor,
  usedInputProperties = [],
}) => {
  const { portalRoot } = usePortalRoot(); // Get portalRoot from context
  const content = transform.content as PlaybookSpreading;

  const editorRef = useRef<HTMLDivElement>(null); // Ref for the editor container
  const listboxButtonRef = useRef<HTMLButtonElement>(null); // Ref for the ListboxButton
  const listboxOptionsPortalRef = useRef<HTMLDivElement>(null); // New ref for the ListboxOptionsPortal

  const updateContent = (updates: Partial<PlaybookSpreading>) => {
    onUpdate({
      content: { ...content, ...updates }
    });
  };

  // Helper to detect data type from available properties
  const detectDataType = (propertyPath: string, properties: Parameter[]): string => {
    const property = properties.find(p => p.fullPath === propertyPath || p.path === propertyPath);
    if (property && property.dataTypes && property.dataTypes.length > 0) {
      return property.dataTypes[0]; // Return the first data type
    }
    return 'object'; // Default for spreading
  };

  // Generate preview of inferred output properties
  const generatePreviewProperties = useCallback(() => {
    if (!content.inputKey || !content.prefix) return [];
    
    // This is a simplified preview - in reality, this would be based on actual input data
    const exampleProperties = [
      { path: `${content.prefix}${content.separator}name`, dataType: 'string', description: 'Spread property: name' },
      { path: `${content.prefix}${content.separator}age`, dataType: 'number', description: 'Spread property: age' },
      { path: `${content.prefix}${content.separator}email`, dataType: 'string', description: 'Spread property: email' },
    ];

    if ((content.depth || 0) > 1) {
      exampleProperties.push(
        { path: `${content.prefix}${content.separator}address${content.separator}street`, dataType: 'string', description: 'Nested spread property: address.street' },
        { path: `${content.prefix}${content.separator}address${content.separator}city`, dataType: 'string', description: 'Nested spread property: address.city' }
      );
    }

    return exampleProperties;
  }, [content.inputKey, content.prefix, content.separator, content.depth]);

  const previewProperties = generatePreviewProperties();

  // Update inferred properties when relevant content changes
  useEffect(() => {
    if (content.inputKey && content.prefix) {
      const inferredProperties = generatePreviewProperties();
      updateContent({ inferredOutputProperties: inferredProperties });
    }
  }, [content.inputKey, content.prefix, content.separator, content.depth, generatePreviewProperties]);

  const handleEventSelectChange = useCallback((selectedEventIds: string[]) => {
    onUpdate({ 
      eventIds: selectedEventIds,
      // Preserve existing content and type information
      content: transform.content,
      inputType: transform.inputType,
      outputType: transform.outputType
    });
  }, [onUpdate, transform.content, transform.inputType, transform.outputType]);

  const currentEventAssociations = uiEventConfigs.filter(event =>
    (transform.eventIds || []).includes(event.id)
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseEditor();
    }
  }, [onCloseEditor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the editor and also outside the ListboxOptionsPortal
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node) &&
        listboxOptionsPortalRef.current &&
        !listboxOptionsPortalRef.current.contains(event.target as Node)
      ) {
        onCloseEditor();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCloseEditor]);

  return (
    <div ref={editorRef} className="flex flex-col gap-3 py-4 px-4" onKeyDown={handleKeyDown}>
      {/* Input Key */}
      <PropertyPathAutocomplete
        label="Input Key"
        value={content.inputKey || ''}
        onSelect={(fullPath) => {
          updateContent({ inputKey: fullPath });
          // Update the transform with detected input type and generate inferred properties
          const inputType = detectDataType(fullPath, availableProperties);
          const inferredProperties = generatePreviewProperties();
          onUpdate({
            inputPath: fullPath,
            inputType: inputType,
            content: { ...content, inputKey: fullPath, inferredOutputProperties: inferredProperties }
          });
        }}
        availableProperties={availableProperties}
        usedProperties={usedInputProperties.filter(path => path !== content.inputKey)}
        expectedDataType="any"
        placeholder="e.g., user.metadata"
      />
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
        The object or array property to spread
      </div>

      {/* Prefix */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Prefix <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={content.prefix || ''}
          onChange={(e) => updateContent({ prefix: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          placeholder="e.g., user_meta"
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          Prefix for all spread properties
        </div>
      </div>

      {/* Separator */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Separator
        </label>
        <input
          type="text"
          value={content.separator || ''}
          onChange={(e) => updateContent({ separator: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          placeholder="e.g., . or _"
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          Character(s) to separate nested property names
        </div>
      </div>

      {/* Depth */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Depth
        </label>
        <input
          type="number"
          value={content.depth || 1}
          onChange={(e) => updateContent({ depth: parseInt(e.target.value) || 1 })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          min="1"
          max="10"
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          How many levels deep to spread nested objects (1 = only top level)
        </div>
      </div>

      {/* Array Start Index */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Array Start Index
        </label>
        <input
          type="number"
          value={content.arrayStartIndex || 0}
          onChange={(e) => updateContent({ arrayStartIndex: parseInt(e.target.value) || 0 })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
          min="0"
        />
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          Starting index for array elements (0 = zero-based, 1 = one-based)
        </div>
      </div>

      {/* Configure Spreading Button */}
      {/* <div className="flex justify-center">
        <button className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center">
          <Wand2 className="h-4 w-4 mr-2" />
          Configure Spreading (AI)
        </button>
      </div> */}

      {/* Assigned Events */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Assigned Events
        </label>
        <div className="flex flex-row gap-2 items-end w-full">
          <Listbox
            value={currentEventAssociations}
            onChange={(selectedEvents: PlaybookEventConfig[]) => handleEventSelectChange(selectedEvents.map(event => event.id))}
            multiple
          >
            {({ open }) => (
              <div className="relative flex-1">
                <ListboxButton
                  ref={listboxButtonRef} // Added ref
                  className={clsx(
                    "relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm",
                    "border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20",
                    "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  )}
                >
                  <div className="flex flex-wrap gap-1 pr-8">
                    {currentEventAssociations.length > 0 ? (
                      currentEventAssociations.map(event => (
                        <span
                          key={event.id}
                          className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {event.name}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault(); // Add preventDefault to stop default ListboxButton behavior
                              handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                              }
                            }}
                            className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                          >
                            <span className="sr-only">Remove {event.name}</span>
                            <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </span>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                {portalRoot && (
                  <ListboxOptionsPortal
                    triggerRef={listboxButtonRef}
                    portalRoot={portalRoot}
                    isOpen={open}
                    forwardedRef={listboxOptionsPortalRef}
                  >
                    {uiEventConfigs.map((event) => (
                      <ListboxOption
                        key={event.id}
                        className={({ active }) =>
                          clsx(
                            active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                            'relative cursor-pointer select-none py-2 pl-10 pr-4' // Changed cursor-default to cursor-pointer
                          )
                        }
                        value={event}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                              {event.name}
                            </span>
                            {selected ? (
                              <span
                                className={clsx(
                                  active ? 'text-white' : 'text-blue-600',
                                  'absolute inset-y-0 left-0 flex items-center pl-3'
                                )}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptionsPortal>
                )}
              </div>
            )}
          </Listbox>
          <button
            type="button"
            onClick={() => {
              handleEventSelectChange(uiEventConfigs.map(event => event.id));
            }}
            disabled={(transform.eventIds || []).length === uiEventConfigs.length}
            className={clsx(
              "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
              "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600",
              (transform.eventIds || []).length === uiEventConfigs.length ? 'opacity-60 cursor-not-allowed' : ''
            )}
          >
            Assign All
          </button>
        </div>
      </div>

      {/* Output Preview */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Output Preview
        </h5>
        
        {content.inputKey && content.prefix ? (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 dark:text-gray-500 mb-2">
              Inferred Output Properties:
            </div>
            {previewProperties.map((prop, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="font-mono text-blue-600 dark:text-blue-400">{prop.path}</span>
                <span className="text-gray-500 dark:text-gray-400">{prop.dataType}</span>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="text-xs text-gray-500 dark:text-gray-500">
                Example: If input object has properties {`{name: "John", age: 30}`}, 
                it will create <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{content.prefix}{content.separator}name</code> and{' '}
                <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">{content.prefix}{content.separator}age</code>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Enter input key and prefix to see output preview
          </div>
        )}
      </div>
    </div>
  );
};

export default SpreadingEditor;

interface ListboxOptionsPortalProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  portalRoot: HTMLElement;
  isOpen: boolean;
  forwardedRef: React.RefObject<HTMLDivElement>; 
}

const ListboxOptionsPortal: React.FC<ListboxOptionsPortalProps> = ({
  children,
  triggerRef,
  portalRoot,
  isOpen,
  forwardedRef,
}) => {
  const { tooltipStyle, tooltipRef } = useTooltipPosition(triggerRef, {
    side: 'bottom',
    align: 'start',
    sideOffset: 2,
    portalRoot,
    isOpen,
  });

  useEffect(() => {
    if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = tooltipRef.current;
    }
  }, [forwardedRef, tooltipRef]);

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      style={{ ...tooltipStyle, width: tooltipStyle.width || 'auto' }}
      className="pointer-events-auto z-50"
    >
      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ListboxOptions
          static
          className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
            dark:bg-gray-800 dark:border dark:border-gray-700"
        >
          {children}
        </ListboxOptions>
      </Transition>
    </div>,
    portalRoot
  );
};
